import React, { useEffect, useMemo, useState } from "react";
import { Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPut } from "../../utils/api";
import { displayName } from "../../utils/displayName";
import { toast } from "../../utils/toast";
import "./cfo-salary-config.css";

const DEFAULT_SALARY = 50000;

const getAgentConfigStorageKey = () => {
  const tenantId = sessionStorage.getItem("tenantId") || "default";
  return `cfoAgentConfigMonthly_v1_${tenantId}`;
};

const getLegacySalaryStorageKey = () => {
  const tenantId = sessionStorage.getItem("tenantId") || "default";
  return `cfoSalaryConfigMonthly_v1_${tenantId}`;
};

const getLegacyAnnualSalaryStorageKey = () => {
  const tenantId = sessionStorage.getItem("tenantId") || "default";
  return `cfoSalaryConfig_v1_${tenantId}`;
};

const getTeamsStorageKey = () => {
  const tenantId = sessionStorage.getItem("tenantId") || "default";
  return `cfoTeamList_v1_${tenantId}`;
};

const CFOSalaryConfig = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [agentConfig, setAgentConfig] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(getAgentConfigStorageKey()) || "{}");
      if (saved && Object.keys(saved).length > 0) return saved;

      const monthly = JSON.parse(localStorage.getItem(getLegacySalaryStorageKey()) || "{}");
      if (monthly && Object.keys(monthly).length > 0) {
        const migrated = {};
        Object.keys(monthly).forEach((k) => {
          migrated[k] = { teamName: "", monthlySalary: Number(monthly[k] || 0) };
        });
        return migrated;
      }

      // Backward compatibility: convert old annual values to monthly.
      const legacyAnnual = JSON.parse(localStorage.getItem(getLegacyAnnualSalaryStorageKey()) || "{}");
      const converted = {};
      Object.keys(legacyAnnual || {}).forEach((k) => {
        converted[k] = { teamName: "", monthlySalary: Number(legacyAnnual[k] || 0) / 12 };
      });
      return converted;
    } catch {
      return {};
    }
  });
  const [bulkSalary, setBulkSalary] = useState(DEFAULT_SALARY);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [agentsRes, configRes, deptsRes] = await Promise.all([
          apiGet("/api/agents"),
          apiGet("/api/cfo-config"),
          apiGet("/api/departments"),
        ]);

        const result = await agentsRes.json().catch(() => null);
        if (!agentsRes.ok || !Array.isArray(result)) {
          toast.error("Failed to load agents");
          return;
        }

        const normalized = result
          .map((agent) => ({
            agentId: agent.agentId,
            hostname: agent.hostname || "",
            name: agent.name || "",
          }))
          .filter((agent) => !!agent.agentId)
          .sort((a, b) => {
            const aLabel = (a.name || a.hostname || a.agentId).toLowerCase();
            const bLabel = (b.name || b.hostname || b.agentId).toLowerCase();
            return aLabel.localeCompare(bLabel);
          });

        setEmployees(normalized);

        const configData = await configRes.json().catch(() => null);
        let loadedConfig = null;
        if (configRes.ok && configData?.success) {
          if (configData.salaryMap && typeof configData.salaryMap === "object") {
            loadedConfig = configData.salaryMap;
          }
        }

        // Use departments from Settings as the single source of truth for teams
        const deptsData = await deptsRes.json().catch(() => null);
        const deptByAgent = {};
        if (deptsRes.ok) {
          // Handle both { success, departments: [...] } and plain array formats
          const deptsList = deptsData?.departments || (Array.isArray(deptsData) ? deptsData : []);
          const deptNames = deptsList.map((d) => d.name || d).filter(Boolean);
          setTeams(deptNames.sort((a, b) => String(a).localeCompare(String(b))));

          deptsList.forEach((d) => {
            const deptName = d.name || "";
            if (!deptName) return;
            (d.agents || []).forEach((agentId) => {
              deptByAgent[agentId] = deptName;
            });
          });
        }

        // Default each employee's team name to their assigned department
        // (from Settings) when no team has been set yet, so the dropdown
        // isn't left blank for agents already grouped into a team elsewhere.
        setAgentConfig((prev) => {
          const base = loadedConfig || prev || {};
          const next = { ...base };
          normalized.forEach((emp) => {
            const existing = next[emp.agentId];
            const assignedTeam = deptByAgent[emp.agentId];
            if (!existing?.teamName && assignedTeam) {
              next[emp.agentId] = {
                teamName: assignedTeam,
                monthlySalary: Number(existing?.monthlySalary || 0),
              };
            }
          });
          return next;
        });
      } catch (err) {
        console.error("CFO salary config fetch error:", err);
        toast.error("Failed to load agents");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const configuredCount = useMemo(
    () => employees.filter((e) => Number(agentConfig[e.agentId]?.monthlySalary || 0) > 0).length,
    [employees, agentConfig]
  );

  useEffect(() => {
    // Backfill teams list from existing mappings to avoid losing old team names.
    const mappedTeams = Object.values(agentConfig || {})
      .map((row) => String(row?.teamName || "").trim())
      .filter(Boolean);
    if (mappedTeams.length === 0) return;
    setTeams((prev) => {
      const merged = Array.from(new Set([...(prev || []), ...mappedTeams])).sort((a, b) => a.localeCompare(b));
      return merged;
    });
  }, [agentConfig]);

  const applySalaryToAll = () => {
    const value = Number(bulkSalary || 0);
    const next = { ...agentConfig };
    employees.forEach((e) => {
      next[e.agentId] = {
        teamName: String(next[e.agentId]?.teamName || ""),
        monthlySalary: value,
      };
    });
    setAgentConfig(next);
  };

  const saveConfig = async () => {
    try {
      const res = await apiPut("/api/cfo-config", { agentConfig, teams });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        toast.error(data?.message || "Failed to save configuration");
        return;
      }

      // keep local backup for offline/fallback compatibility
      localStorage.setItem(getAgentConfigStorageKey(), JSON.stringify(agentConfig));
      localStorage.setItem(getTeamsStorageKey(), JSON.stringify(teams));
      toast.success("Agent configuration saved");
    } catch (err) {
      toast.error("Failed to save configuration");
    }
  };



  return (
    <div className="cfo-salary-page">
      <div className="cfo-salary-header">
        <div>
          <h1>Configure Agents</h1>
          <p>Set team name and monthly salary for each employee in one place.</p>
        </div>
        <div className="cfo-salary-actions">
          <button type="button" className="btn-cfo-primary" onClick={saveConfig}>
            <Save size={15} />
            Save
          </button>
        </div>
      </div>

      <div className="cfo-salary-card">

        <div className="cfo-salary-toolbar">
          <div className="cfo-count">Salary set: {configuredCount}/{employees.length}</div>
          <label>
            Apply to all (INR/month)
            <input
              type="number"
              min="0"
              step="1000"
              value={bulkSalary}
              onChange={(e) => setBulkSalary(Number(e.target.value || 0))}
            />
          </label>
          <button type="button" className="btn-cfo-light" onClick={applySalaryToAll}>Apply</button>
        </div>

        {loading ? (
          <div className="cfo-salary-empty">Loading agents...</div>
        ) : (
          <div className="cfo-salary-table-wrap">
            <table className="cfo-salary-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Team Name</th>
                  <th>Monthly Salary (INR)</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr key={emp.agentId}>
                    <td>{displayName(emp)}</td>
                    <td>
                      <select
                        value={String(agentConfig[emp.agentId]?.teamName || "")}
                        onChange={(e) => {
                          const value = e.target.value;
                          setAgentConfig((prev) => ({
                            ...prev,
                            [emp.agentId]: {
                              teamName: value,
                              monthlySalary: Number(prev[emp.agentId]?.monthlySalary || 0),
                            },
                          }));
                        }}
                      >
                        <option value="">Select Team</option>
                        {teams.map((team) => (
                          <option key={team} value={team}>{team}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        value={Number(agentConfig[emp.agentId]?.monthlySalary || 0)}
                        onChange={(e) => {
                          const value = Number(e.target.value || 0);
                          setAgentConfig((prev) => ({
                            ...prev,
                            [emp.agentId]: {
                              teamName: String(prev[emp.agentId]?.teamName || ""),
                              monthlySalary: value,
                            },
                          }));
                        }}
                      />
                    </td>
                  </tr>
                ))}
                {employees.length === 0 && (
                  <tr>
                    <td colSpan={3} className="cfo-salary-empty">No agents found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CFOSalaryConfig;
